import type { CharacterAsset } from './types';

export const PARAM_TOPIC = 'avatar.params.v1';
export const EVENT_TOPIC = 'avatar.events.v1';
export const COMMAND_TOPIC = 'avatar.commands.v1';
export const CLIENT_METRICS_TOPIC = 'avatar.metrics.v1';
export const DRIVE_MAGIC = 0xa7;
export const LEGACY_PARAM_SCHEMA_VERSION = 1;
export const SUPPORTED_PARAM_SCHEMA_VERSIONS = [LEGACY_PARAM_SCHEMA_VERSION] as const;
export const DRIVE_PACKET_SIZE = 23;

export interface ParamSchemaLayout {
  mouthParams: readonly string[];
  faceParams: readonly string[];
  packetSize: number;
}

const PARAM_SCHEMA_LAYOUTS = new Map<number, ParamSchemaLayout>([
  [LEGACY_PARAM_SCHEMA_VERSION, {
    mouthParams: ['open', 'wide', 'round', 'smile', 'frown', 'jaw', 'upperLip', 'lowerLip'],
    faceParams: ['blinkLeft', 'blinkRight', 'gazeX', 'gazeY', 'headX', 'headY', 'breath', 'emotion'],
    packetSize: DRIVE_PACKET_SIZE,
  }],
]);

export interface DriveFrame {
  seq: number;
  audioPtsMs: number;
  mouth: Float32Array;
  face: Float32Array;
}

export function readParamSchemaVersion(
  value: unknown,
  fallback = LEGACY_PARAM_SCHEMA_VERSION,
): number {
  const version = value === undefined ? fallback : value;
  if (typeof version !== 'number' || !Number.isSafeInteger(version) || version < 1) {
    throw new Error(`invalid param schema version: ${String(version)}`);
  }
  return version;
}

export function requireSupportedParamSchemaVersion(version: number): ParamSchemaLayout {
  const layout = PARAM_SCHEMA_LAYOUTS.get(version);
  if (!layout) {
    throw new Error(
      `unsupported param schema version ${version}; supported: ${SUPPORTED_PARAM_SCHEMA_VERSIONS.join(', ')}`,
    );
  }
  return layout;
}

export function supportsParamSchemaVersion(version: number): boolean {
  return PARAM_SCHEMA_LAYOUTS.has(version);
}

export function assertCharacterAssetParamSchema(
  asset: CharacterAsset,
  version: number,
): void {
  const layout = requireSupportedParamSchemaVersion(version);
  const mouthParams = asset.parameters?.mouth;
  const faceParams = asset.parameters?.face;
  if (
    asset.schemaVersion !== version
    || !Array.isArray(mouthParams)
    || !Array.isArray(faceParams)
    || !sameParameterNames(mouthParams, layout.mouthParams)
    || !sameParameterNames(faceParams, layout.faceParams)
  ) {
    throw new Error(
      `character asset parameters do not match param schema ${version}: `
      + `expected asset schemaVersion ${version}, mouth[${layout.mouthParams.length}], `
      + `and face[${layout.faceParams.length}] with the canonical parameter order`,
    );
  }
}

function sameParameterNames(actual: string[], expected: readonly string[]): boolean {
  return actual.length === expected.length
    && actual.every((name, index) => name === expected[index]);
}

export function decodeDriveFrame(
  payload: Uint8Array,
  paramSchemaVersion = LEGACY_PARAM_SCHEMA_VERSION,
): DriveFrame {
  const layout = requireSupportedParamSchemaVersion(paramSchemaVersion);
  if (payload.byteLength !== layout.packetSize) {
    throw new Error(`invalid drive packet length: ${payload.byteLength}`);
  }
  const view = new DataView(payload.buffer, payload.byteOffset, payload.byteLength);
  if (view.getUint8(0) !== DRIVE_MAGIC) {
    throw new Error('invalid drive packet magic');
  }
  const mouth = new Float32Array(layout.mouthParams.length);
  const face = new Float32Array(layout.faceParams.length);
  for (let index = 0; index < layout.mouthParams.length; index += 1) {
    mouth[index] = view.getUint8(7 + index) / 255;
  }
  const faceOffset = 7 + layout.mouthParams.length;
  for (let index = 0; index < layout.faceParams.length; index += 1) {
    face[index] = view.getUint8(faceOffset + index) / 255;
  }
  return {
    seq: view.getUint16(1, false),
    audioPtsMs: view.getUint32(3, false),
    mouth,
    face,
  };
}

export function isNewerSequence(sequence: number, previous: number): boolean {
  const difference = (sequence - previous + 0x10000) & 0xffff;
  return difference > 0 && difference < 0x8000;
}

export class ParameterTimeline {
  private frames: DriveFrame[] = [];
  private lastSequence: number | undefined;
  private lastPts = 0;

  reset(): void {
    this.frames = [];
    this.lastSequence = undefined;
    this.lastPts = 0;
  }

  push(frame: DriveFrame): boolean {
    if (this.lastSequence !== undefined && !isNewerSequence(frame.seq, this.lastSequence)) {
      return false;
    }
    if (frame.audioPtsMs + 100 < this.lastPts) {
      this.reset();
    }
    this.lastSequence = frame.seq;
    this.lastPts = frame.audioPtsMs;
    this.frames.push(frame);
    if (this.frames.length > 80) this.frames.shift();
    return true;
  }

  sample(audioPtsMs: number): DriveFrame | undefined {
    if (this.frames.length === 0) return undefined;
    while (this.frames.length > 2 && (this.frames[1]?.audioPtsMs ?? Infinity) < audioPtsMs - 120) {
      this.frames.shift();
    }
    const rightIndex = this.frames.findIndex((frame) => frame.audioPtsMs >= audioPtsMs);
    if (rightIndex === -1) return this.frames[this.frames.length - 1];
    const right = this.frames[rightIndex];
    if (rightIndex === 0 || !right) return right;
    const left = this.frames[rightIndex - 1];
    if (!left || right.audioPtsMs <= left.audioPtsMs) return right;
    const ratio = Math.min(1, Math.max(0, (audioPtsMs - left.audioPtsMs) / (right.audioPtsMs - left.audioPtsMs)));
    return {
      seq: right.seq,
      audioPtsMs,
      mouth: interpolate(left.mouth, right.mouth, ratio),
      face: interpolate(left.face, right.face, ratio),
    };
  }
}

function interpolate(left: Float32Array, right: Float32Array, ratio: number): Float32Array {
  const output = new Float32Array(left.length);
  for (let index = 0; index < output.length; index += 1) {
    output[index] = (left[index] ?? 0) + ((right[index] ?? 0) - (left[index] ?? 0)) * ratio;
  }
  return output;
}
